<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <div v-if="visible" :class="['message-container', type]" @click.stop="close">
    <div @click="$event.stopPropagation()" :class="['message', type]">
      <a
        class="message-close hitems-close"
        v-if="showClose"
        href="javascript:;"
        @click.stop="close"
      ></a>
      <template v-if="type === 'success'">
        <p class="title-icon" v-if="showIcon">
          <img src="@/assets/images/success-icon.png" width="48" />
        </p>
      </template>
      <template v-if="type === 'error'">
        <p class="title-icon" v-if="showIcon">
          <img src="@/assets/images/error-icon.png" width="48" />
        </p>
      </template>
      <h3 class="title font-bold" v-if="props.title">{{ props.title }}</h3>
      <p class="content">
        {{ message }}
        <slot :close="close"></slot>
      </p>
      <p class="message-btns" v-if="showConfirm">
        <a
          class="message-btn font-bold message-btn-confirm"
          v-if="showConfirm"
          href="javascript:;"
          @click.stop="confirm"
        >
          {{ confirmText }}
          <span v-if="confirmSuffixText" class="message-btn-confirm-suffix">
            {{ confirmSuffixText }}
          </span>
        </a>
      </p>
      <p class="message-btns" v-if="showCancel">
        <a
          class="message-btn font-bold message-btn-cancel"
          v-if="showCancel"
          href="javascript:;"
          @click.stop="cancel"
        >
          {{ cancelText }}
        </a>
      </p>
      <slot name="footer"></slot>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { ref, onMounted } from 'vue'
  import { useI18n } from 'vue-i18n'
  // 定义 props 的类型
  interface Props {
    title?: string
    message?: string
    type?: 'success' | 'error' | 'info' | 'warning'
    timer?: number
    config?: {
      showClose?: boolean
      showIcon?: boolean
      showCancel?: boolean
      showConfirm?: boolean
      cancelText?: string
      confirmText?: string
      confirmSuffixText?: string
    }
  }

  // 获取 props
  const props = defineProps<Props>()
  const emit = defineEmits<{
    (e: 'close'): void
    (e: 'confirm'): void
    (e: 'cancel'): void
  }>()
  const { t } = useI18n()
  const showClose = props.config?.showClose === undefined ? true : props.config?.showClose
  const showIcon = props.config?.showIcon === undefined ? true : props.config?.showIcon
  const showCancel = props.config?.showCancel === undefined ? false : props.config?.showCancel
  const showConfirm = props.config?.showConfirm === undefined ? false : props.config?.showConfirm
  const cancelText =
    props.config?.cancelText === undefined ? t('appMessage.canceltext') : props.config?.cancelText
  const confirmText =
    props.config?.confirmText === undefined
      ? t('appMessage.confirmtext')
      : props.config?.confirmText
  const confirmSuffixText = props.config?.confirmSuffixText
  // 0表示不自动关闭
  let timer = props.timer === undefined ? 3000 : props.timer
  const visible = ref(true)

  // 关闭消息的方法
  const close = () => {
    visible.value = false
    setTimeout(() => {
      emit('close')
    }, 300) // 等待动画完成后再发出关闭事件
  }

  const confirm = () => {
    emit('confirm')
    close()
  }

  const cancel = () => {
    emit('cancel')
    close()
  }

  // 自动关闭消息
  onMounted(() => {
    timer !== 0 && setTimeout(close, timer) // 3秒后自动关闭
  })
</script>

<style scoped>
  .message-container {
    position: fixed;
    top: 0;
    bottom: 0;
    left: 0;
    z-index: 999;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.3);
  }

  .message-close {
    position: absolute;
    top: 0;
    right: -46px;
    z-index: 1;
    display: flex;
    justify-content: center;
    align-items: center;
    width: clamp(20px, 3.2rem, 32px);
    height: clamp(20px, 3.2rem, 32px);
    color: #aaa;
    font-size: 20px;
    background-color: #fff;
    border-radius: 50%;
    transition: all 0.3s ease-in-out;
  }

  .message-close:hover {
    background-color: rgba(255, 255, 255, 0.8);
  }

  .message {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 380px;
    min-height: 180px;
    padding: 40px 28px 24px 28px;
    color: #666;
    background: #fff;
    border-radius: 18px;
    transition: opacity 0.3s ease;
  }

  .title {
    color: #333;
    font-size: 17px;
    font-weight: bold;
    text-align: center;
  }

  .title-icon {
    margin-bottom: 20px;
  }

  .content {
    width: 100%;
    margin: 16px 0 24px 0;
    color: #666;
    font-size: 14px;
    line-height: 1.5;
    letter-spacing: -0.2px;
    text-align: center;
    word-wrap: break-word;
    white-space: pre-line;
  }

  .message.info {
    min-height: auto;
    max-height: auto;
    padding: 10px;
    border-color: transparent;
  }

  .message.info .content {
    width: 90%;
    margin: 0;
  }

  .message.warning {
    border-color: #ffc107;
  }

  .message-btns {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 40px;
    margin-top: 16px;
  }

  .message-btn {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 40px;
    color: #333;
    font-size: 14px;
    font-weight: 600;
    border: 1px solid #333;
    border-radius: 30px;
  }

  .message-btn-cancel:hover {
    border: 1px solid var(--primary-color);
    color: var(--primary-color);
  }

  .message-btn-confirm {
    border-color: var(--primary-color);
    background: var(--primary-color);
    color: #fff;
  }

  .message-btn-confirm-suffix {
    color: rgba(255, 255, 255, 0.75);
    font-size: 11px;
    font-weight: 400;
    line-height: 20px;
  }

  .message-btn-confirm:hover {
    background: var(--primary-color-hover);
  }

  .message-btn-confirm:active {
    background: var(--primary-color);
  }
</style>
